import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardTable from './DashboardTable';
import IconButton from './IconButton';
import Pagination from '../../../components/UI/TableGrid/Pagination';
import AddEmployee from '../../../containers/EmployeeContainer/AddEmployee';
import Alert from '../../../components/UI/ConfirmBox/Alert';
import VisitsService from '../../../services/VisitsService';
import PatientChartsService from '../../../services/PatientChartsService';
import EmrVisitsService from '../../../services/EmrVisitsService';
import FileManagerService from '../../../services/FileManagerService';
import VisitDetails from '../../EmployeeVisitContainer/VisitDetails';

const Visits = ({ data = [], onActionClick, isLoading = false, onExpand, profile, Loader, Toast }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [initialPosition, setInitialPosition] = useState(null);
  const containerRef = useRef(null);
  const originalPositionRef = useRef(null);
  const [allData, setAllData] = useState([]); // Store all data for pagination
  const [tableData, setTableData] = useState([]); // Paginated data to display
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalRecords: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [patientName, setPatientName] = useState('');
  const [sortColumn, setSortColumn] = useState('visit_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Modal state for Add Employee
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [visitToDelete, setVisitToDelete] = useState(null);
  const [isVisitDetailViewOpen, setIsVisitDetailViewOpen] = useState(false);
  const [visitDetailViewId, setVisitDetailViewId] = useState(null);
  const [snapEmployeeDetails, setSnapEmployeeDetails] = useState(null);

  // Employer dashboard: show only Work Comp and Employer Services visits.
  // API and controller filter to these types; this is a UI safety net.
  const filteredData = useMemo(() => {
    if (!data || !data.length) return [];
    return data.filter((visit) => {
      const type = (visit.type || visit.visit_category || '').toLowerCase();
      return type.includes('work comp') || type.includes('employer services');
    });
  }, [data]);

  // When small card has no data after load, trigger refetch (same path as fullscreen)
  // so we get visits with the correct payload instead of the initial empty response.
  const hasRefetchedEmptyRef = useRef(false);
  useEffect(() => {
    if (isFullscreen || isLoading || !onExpand) return;
    if (data && data.length > 0) return;
    if (hasRefetchedEmptyRef.current) return;
    hasRefetchedEmptyRef.current = true;
    const id = setTimeout(() => {
      onExpand(false, 'All', '', sortColumn, sortDirection);
    }, 300);
    return () => clearTimeout(id);
  }, [isFullscreen, isLoading, data, onExpand, sortColumn, sortDirection]);

  // Update allData and tableData when data changes
  useEffect(() => {
    if (isFullscreen) {
      // When fullscreen, store all filtered data
      setAllData(filteredData);
      // Reset pagination to page 1 when data changes (e.g., after search)
      setPagination(prev => ({ ...prev, page: 1, totalRecords: filteredData.length }));
    } else {
      // When not fullscreen, use incoming filtered data from parent (API-sorted)
      setTableData(filteredData);
      setAllData([]);
      setPagination({ page: 1, perPage: 10, totalRecords: 0 });
      setSearchTerm('');
      setPatientName('');
      // Don't reset sortColumn/sortDirection - preserve user's sort selection
    }
  }, [filteredData, isFullscreen]);

  // Apply pagination when in fullscreen (sort and search are API-based)
  useEffect(() => {
    if (isFullscreen) {
      // If no data, set empty tableData and reset pagination
      if (allData.length === 0) {
        setTableData([]);
        setPagination(prev => ({ ...prev, totalRecords: 0, page: 1 }));
        return;
      }
      
      // Fullscreen: data is already sorted and filtered by API; only paginate
      const totalRecords = allData.length;
      const maxPage = Math.max(1, Math.ceil(totalRecords / pagination.perPage));
      const currentPage = pagination.page > maxPage ? 1 : pagination.page;
      
      setPagination(prev => ({ 
        ...prev, 
        totalRecords: totalRecords,
        page: currentPage
      }));
      
      const start = (currentPage - 1) * pagination.perPage;
      const end = start + pagination.perPage;
      setTableData(allData.slice(start, end));
    }
  }, [allData, isFullscreen, pagination.page, pagination.perPage]);


  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
  };

  // Open visit charting only for Employer Services (not for Work Comp)
  const isEmployerServicesCategory = (category) =>
    typeof category === 'string' && category.startsWith('Employer Services');

  const handleVisitTypeClick = (row) => {
    const visitId = row.visit_id || row.id;
    if (!visitId) return;
    navigate(`/visits/visit-details/${visitId}`);
  };

  const getCellContent = (col, row) => {
    if (col !== 'TYPE') return null;
    const category = row.visit_category ?? row.type ?? '';
    const typeLabel = category || '-';
    // Only Employer Services: clickable link to open charting; Work Comp: plain text
    if (!isEmployerServicesCategory(category)) return null;
    return (
      <a
        href="#"
        className="text-dark"
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.preventDefault();
          handleVisitTypeClick(row);
        }}
        title="Open visit charting"
      >
        {typeLabel}
      </a>
    );
  };

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
    if (onExpand) {
      setPagination(prev => ({ ...prev, page: 1 }));
      onExpand(isFullscreen, activeTab, patientName, column, direction);
    }
  };

  // const handleSearchChange = (e) => {
  //   setSearchTerm(e.target.value);
  //   setPagination(prev => ({ ...prev, page: 1 }));
  // };

  const handleTabClick = (tab, e) => {
    e.preventDefault();
    setActiveTab(tab);
    if (onExpand) {
      onExpand(isFullscreen, tab, patientName, sortColumn, sortDirection);
    }
  };

  const handleScheduleClick = () => {
    navigate('/appointments');
  };

  const handleAddEmployeeClick = () => {
    setShowAddEmployeeModal(true);
  };

  const handleAddEmployeeModalClose = (shouldClose, action, employeeData) => {
    if (shouldClose === false) {
      setShowAddEmployeeModal(false);
    }
    // If employee was added successfully, you might want to refresh the data
    // or handle the employeeData if needed
  };

  // Handle action clicks from DashboardTable
  const handleActionClick = (row, action) => {
    const visitId = row.visit_id || row.id;
    
    switch (action) {
      case 'detail':
        if (!visitId) return;
        setVisitDetailViewId(visitId);
        setSnapEmployeeDetails(typeof row.employee === 'object' ? row.employee : null);
        setIsVisitDetailViewOpen(true);
        break;
      case 'delete':
        setVisitToDelete({ id: visitId, row });
        setShowDeleteModal(true);
        break;
      case 'clearance':
        handleDownloadClearanceReport(visitId);
        break;
      case 'workStatus':
        handleDownloadWorkStatusForm(visitId);
        break;
      default:
        // Fallback to parent's onActionClick if provided
        if (onActionClick) {
          onActionClick(row, action);
        }
    }
  };

  const visitDetailViewModal = () => {
    setIsVisitDetailViewOpen(false);
    setVisitDetailViewId(null);
    setSnapEmployeeDetails(null);
  };

  // Delete visit handler with confirmation
  const handleDeleteVisit = async () => {
    if (!visitToDelete) return;
    
    try {
      if (Loader && typeof Loader === 'function') {
        Loader(true);
      }
      await VisitsService.deleteVisits(visitToDelete.id);
      if (Toast && typeof Toast.success === 'function') {
        Toast.success("Visit deleted successfully");
      }
      setShowDeleteModal(false);
      setVisitToDelete(null);
      if (onExpand) {
        onExpand(isFullscreen, activeTab, patientName, sortColumn, sortDirection);
      }
    } catch (error) {
      console.error('Error deleting visit:', error);
      if (Toast && typeof Toast.error === 'function') {
        Toast.error("Failed to delete visit");
      }
    } finally {
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    }
  };

  // Download clearance report handler
  const handleDownloadClearanceReport = async (visitId) => {
    if (!visitId) return;
    
    if (Loader && typeof Loader === 'function') {
      Loader(true);
    }
    
    try {
      const data = await PatientChartsService.getClearanceReportPDF(null, visitId);
      
      // Check if data is empty, null, undefined, or empty string
      const isEmpty = !data || 
                     data === '' || 
                     data === null || 
                     data === undefined ||
                     (typeof data === 'string' && data.trim() === '') ||
                     (typeof data === 'object' && Object.keys(data).length === 0);
      
      if (isEmpty) {
        console.log("Clearance report data is empty:", data);
        if (Toast && typeof Toast.error === 'function') {
          Toast.error("Clearance report is not available");
        } else {
          console.warn("Toast is not available or Toast.error is not a function", Toast);
        }
        if (Loader && typeof Loader === 'function') {
          Loader(false);
        }
        return;
      }
      
      const pdfData = {
        file_content: data,
        original_name: "clearance-report",
        mime_type: "application/pdf",
      };
      
      FileManagerService.printPdf(pdfData);
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    } catch (error) {
      console.error("Error downloading clearance report:", error);
      if (Toast && typeof Toast.error === 'function') {
        Toast.error("Clearance report is not available");
      } else {
        console.warn("Toast is not available or Toast.error is not a function", Toast);
      }
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    }
  };

  // Download work status form handler
  const handleDownloadWorkStatusForm = async (visitId) => {
    if (!visitId) return;
    
    if (Loader && typeof Loader === 'function') {
      Loader(true);
    }
    
    try {
      const response = await EmrVisitsService.printWorkStatusForm(visitId);
      
      // Handle different response structures
      const base64Data = typeof response === 'string' ? response : (response?.data || response);
      
      // Check if data is empty
      const isEmpty = !base64Data || 
                     base64Data === '' || 
                     base64Data === null || 
                     base64Data === undefined ||
                     (typeof base64Data === 'string' && base64Data.trim().length === 0) ||
                     (typeof base64Data === 'object' && Object.keys(base64Data).length === 0);
      
      if (isEmpty) {
        console.log("Work status form data is empty:", base64Data);
        if (Toast && typeof Toast.error === 'function') {
          Toast.error("Work status form is not available");
        } else {
          console.warn("Toast is not available or Toast.error is not a function", Toast);
        }
        if (Loader && typeof Loader === 'function') {
          Loader(false);
        }
        return;
      }
      
      const pdfData = {
        file_content: base64Data,
        original_name: "work-status-report",
        mime_type: "application/pdf"
      };
      
      FileManagerService.printPdf(pdfData);
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    } catch (error) {
      console.error("Error downloading work status form:", error);
      if (Toast && typeof Toast.error === 'function') {
        Toast.error("Work status form is not available");
      } else {
        console.warn("Toast is not available or Toast.error is not a function", Toast);
      }
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    }
  };

  const SettingsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.4155 6.58674C10.1962 7.36741 10.1962 8.63474 9.4155 9.41674C8.63483 10.1974 7.3675 10.1974 6.5855 9.41674C5.80483 8.63608 5.80483 7.36874 6.5855 6.58674C7.3675 5.80474 8.63417 5.80474 9.4155 6.58674"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 7.99992C2 7.56059 2.10133 7.12592 2.29733 6.72525V6.72525C3.30733 4.66059 5.53933 3.33325 8 3.33325C10.4607 3.33325 12.6927 4.66059 13.7027 6.72525V6.72525C13.8987 7.12592 14 7.56059 14 7.99992C14 8.43925 13.8987 8.87392 13.7027 9.27458V9.27458C12.6927 11.3393 10.4607 12.6666 8 12.6666C5.53933 12.6666 3.30733 11.3393 2.29733 9.27458V9.27458C2.10133 8.87392 2 8.43925 2 7.99992Z"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4L4 12M4 4L12 12"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div
      ref={containerRef}
      className={`${isFullscreen ? 'tblviewcard flip fullscreen-card' : 'col-lg-6 mb-2 pb-1'}`}
      style={isFullscreen ? {
        position: 'fixed',
        top: initialPosition ? `${initialPosition.top}px` : '0',
        left: initialPosition ? `${initialPosition.left}px` : '0',
        transform: initialPosition ? 'none' : 'none',
        width: initialPosition ? `${initialPosition.width}px` : '100vw',
        height: initialPosition ? `${initialPosition.height}px` : '100vh',
        zIndex: 1040,
        backgroundColor: '#fff',
        padding: initialPosition ? '20px' : '20px',
        borderRadius: initialPosition ? '8px' : '0',
        boxShadow: initialPosition ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
        transition: 'all 0.3s ease-in-out'
      } : { transition: 'all 0.3s ease-in-out' }}
    >
      <div className="card mb-0 border-0 shadow-none" style={isFullscreen ? { height: '100%', margin: 0 } : {}}>
        <div className="card-header sh_accordionheader">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <h6 className="text-md font-weight-bold pr-2 mb-0">Visits</h6>
              <ul className="nav sh_subnav border-0">
                <li>
                  <a 
                    href="#" 
                    onClick={(e) => handleTabClick('All', e)}
                    className={`flex-sm-fill text-sm-center text-dark nav-link ${activeTab === 'All' ? 'active' : ''}`}
                    aria-current={activeTab === 'All' ? 'page' : undefined}
                  >
                    All
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    onClick={(e) => handleTabClick('Scheduled', e)}
                    className={`flex-sm-fill text-sm-center text-dark nav-link ${activeTab === 'Scheduled' ? 'active' : ''}`}
                  >
                    Scheduled
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    onClick={(e) => handleTabClick('Completed', e)}
                    className={`flex-sm-fill text-sm-center text-dark nav-link ${activeTab === 'Completed' ? 'active' : ''}`}
                  >
                    Completed
                  </a>
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-1">
              <div className="px-1">
                <a 
                  className="btn sh_btniconnew" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (!isFullscreen && containerRef.current) {
                      const rect = containerRef.current.getBoundingClientRect();
                      const pos = {
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height
                      };
                      originalPositionRef.current = pos;
                      setInitialPosition(pos);
                      setIsFullscreen(true);
                      // Trigger transition to center after initial position is rendered
                      setTimeout(() => {
                        setInitialPosition(null);
                      }, 50);
                      // Fetch all data when expanding (with sort params)
                      if (onExpand) {
                        onExpand(true, activeTab, '', sortColumn, sortDirection);
                      }
                    } else {
                      // When closing, animate from center back to original position
                      if (originalPositionRef.current) {
                        setInitialPosition(originalPositionRef.current);
                        // Wait for animation to complete, then close
                        setTimeout(() => {
                          setIsFullscreen(false);
                          setInitialPosition(null);
                          originalPositionRef.current = null;
                          setPatientName(''); // Clear patient name when closing
                          // Refetch with limit 5 when closing
                          if (onExpand) {
                            onExpand(false, activeTab, '', sortColumn, sortDirection);
                          }
                        }, 300); // Match transition duration
                      } else {
                        setIsFullscreen(false);
                        setInitialPosition(null);
                        setPatientName(''); // Clear patient name when closing
                        // Refetch with limit 5 when closing
                        if (onExpand) {
                          onExpand(false, activeTab, '', sortColumn, sortDirection);
                        }
                      }
                    }
                  }}
                  title={isFullscreen ? 'Minimize' : 'Maximize'}
                >
                  {isFullscreen ? <CloseIcon /> : <SettingsIcon />}
                </a>
              </div>
              {!isFullscreen && (
              <div>
                  <button 
                    type="button" 
                    className="btn sh_btnlight btn-sm"
                    onClick={handleScheduleClick}
                  >
                    Schedule
                  </button>
                  <button 
                    type="button" 
                    className="btn sh_btn btn-primary"
                    onClick={handleAddEmployeeClick}
                  >
                    Add Employee
                  </button>
              </div>
              )}
            </div>
          </div>
        </div>
        <div className={`card-body ${isFullscreen ? '' : 'sh_dashtableoverflowcard'} px-0 pt-0 mt-2 pb-2`} style={isFullscreen ? { height: 'calc(100% - 60px)', overflow: 'auto', display: 'flex', flexDirection: 'column' } : {}}>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              {/* <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div> */}
            </div>
          ) : (
            <>
              {isFullscreen && (
                <div className="px-3 pt-2 pb-2 d-flex align-items-center justify-content-end" style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <div className="d-flex align-items-center">
                    <div className="mr-2">
                      <input
                        type="text"
                        placeholder="Employee"
                        value={patientName}
                        onChange={(e) => {
                          setPatientName(e.target.value);
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const trimmed = patientName.trim();
                            setPatientName(trimmed);
                            setPagination(prev => ({ ...prev, page: 1 }));
                            if (onExpand) {
                              onExpand(true, activeTab, trimmed, sortColumn, sortDirection);
                            }
                          }
                        }}
                        className="form-control"
                        style={{ minWidth: '200px' }}
                      />
                    </div>
                    <div className="addAction">
                      <div className="btn-group">
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            const trimmed = patientName.trim();
                            setPatientName(trimmed);
                            setPagination(prev => ({ ...prev, page: 1 }));
                            if (onExpand) {
                              onExpand(true, activeTab, trimmed, sortColumn, sortDirection);
                            }
                          }}
                        >
                          Search
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setPatientName('');
                            setPagination(prev => ({ ...prev, page: 1 }));
                            if (onExpand) {
                              onExpand(true, activeTab, '', sortColumn, sortDirection);
                            }
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div style={isFullscreen ? { flex: 1, overflow: 'auto' } : {}}>
                <DashboardTable
                  columns={['EMPLOYEE', 'TYPE', 'VISIT DATE', 'STATUS', 'ACTION']}
                  data={isFullscreen ? tableData : filteredData}
                  onActionClick={handleActionClick}
                  sortable={true}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  emptyMessage="No visits exists"
                  getCellContent={getCellContent}
            actionIcons={[
              {
                svg: (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.7684 8.23223C12.7447 9.20848 12.7447 10.792 11.7684 11.7695C10.7922 12.7457 9.20865 12.7457 8.23115 11.7695C7.2549 10.7932 7.2549 9.20973 8.23115 8.23223C9.2074 7.25598 10.791 7.25598 11.7684 8.23223" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M2.50098 10.0003C2.50098 9.45199 2.62765 8.90949 2.87265 8.40949V8.40949C4.13515 5.82866 6.92598 4.16699 10.001 4.16699C13.076 4.16699 15.8668 5.82866 17.1293 8.40949V8.40949C17.3743 8.90949 17.501 9.45199 17.501 10.0003C17.501 10.5487 17.3743 11.0912 17.1293 11.5912V11.5912C15.8668 14.172 13.076 15.8337 10.001 15.8337C6.92598 15.8337 4.13515 14.172 2.87265 11.5912V11.5912C2.62765 11.0912 2.50098 10.5487 2.50098 10.0003Z" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ),
                title: 'Detail view',
                action: 'detail'
              },
              // {
              //   svg: (
              //     <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              //       <path d="M12.3573 7.6416L7.64062 12.3583" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              //       <path d="M12.3573 12.3583L7.64062 7.6416" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              //       <path fillRule="evenodd" clipRule="evenodd" d="M10 17.5V17.5C5.8575 17.5 2.5 14.1425 2.5 10V10C2.5 5.8575 5.8575 2.5 10 2.5V2.5C14.1425 2.5 17.5 5.8575 17.5 10V10C17.5 14.1425 14.1425 17.5 10 17.5Z" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              //     </svg>
              //   ),
              //   title: 'Delete',
              //   action: 'delete'
              // },
              {
                svg: (
                   <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 5.00008V2.50008C14.9998 2.03991 14.6268 1.66691 14.1667 1.66675H5.83333C5.37316 1.66691 5.00017 2.03991 5 2.50008V5.00008" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14.168 7.49992H15.0013" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 14.1667H4.16667C3.24619 14.1667 2.5 13.4205 2.5 12.5V6.66667C2.5 5.74619 3.24619 5 4.16667 5H15.8333C16.7538 5 17.5 5.74619 17.5 6.66667V12.5C17.5 13.4205 16.7538 14.1667 15.8333 14.1667H15" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12.5 12.4999H7.5" stroke="#323232" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12.5 14.9999H7.5" stroke="#323232" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="5" y="10" width="10" height="7.5" rx="1" stroke="#323232" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ),
                    title: 'Download Clearance Report',
                    action: 'clearance'
              },
              // {
              //   svg: (
              //     <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              //       <path d="M15 5.00008V2.50008C14.9998 2.03991 14.6268 1.66691 14.1667 1.66675H5.83333C5.37316 1.66691 5.00017 2.03991 5 2.50008V5.00008" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              //       <path d="M14.168 7.49992H15.0013" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              //       <path d="M5 14.1667H4.16667C3.24619 14.1667 2.5 13.4205 2.5 12.5V6.66667C2.5 5.74619 3.24619 5 4.16667 5H15.8333C16.7538 5 17.5 5.74619 17.5 6.66667V12.5C17.5 13.4205 16.7538 14.1667 15.8333 14.1667H15" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              //       <path d="M12.5 12.4999H7.5" stroke="#323232" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              //       <path d="M12.5 14.9999H7.5" stroke="#323232" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              //       <rect x="5" y="10" width="10" height="7.5" rx="1" stroke="#323232" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              //     </svg>
              //   ),
              //       title: 'Download Work Status Form',
              //       action: 'workStatus'
              // }
            ]}
          />
              </div>
              {isFullscreen && allData.length > 0 && pagination.totalRecords > pagination.perPage && (
                <div className="px-3 pb-2" style={{ borderTop: '1px solid #e0e0e0' }}>
                  <Pagination
                    pagination={pagination}
                    fetchTableRecords={handlePaginationChange}
                    tableRows={{ data: tableData }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add Employee Modal */}
      <AddEmployee
        isOpen={showAddEmployeeModal}
        modalToogleHandler={handleAddEmployeeModalClose}
        modelTitle="Add Employee"
        profile={profile}
        Loader={Loader}
        Toast={Toast}
        fetchEmployeeList={() => {
          if (onExpand) {
            onExpand(false, activeTab, '', sortColumn, sortDirection);
          }
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Alert
          type="delete"
          contentTitle="Delete Visit"
          content="Are you sure you want to delete this visit?"
          yesButtonName="Delete"
          noButtonName="Cancel"
          onClose={() => {
            setShowDeleteModal(false);
            setVisitToDelete(null);
          }}
          onDelete={handleDeleteVisit}
        />
      )}
      {isVisitDetailViewOpen && (
        <VisitDetails
          isOpen={isVisitDetailViewOpen}
          visitId={visitDetailViewId}
          modalToogleHandler={visitDetailViewModal}
          fetchDetails={() => {
            if (onExpand) {
              onExpand(isFullscreen, activeTab, patientName, sortColumn, sortDirection);
            }
          }}
          snapEmployeeDetails={snapEmployeeDetails}
          profile={profile}
          Loader={Loader}
          Toast={Toast}
          allowDateTimeEdit={true}
        />
      )}
    </div>
  );
};

export default Visits;

